import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Combobox } from "@/modern/components/ui/combobox";
import { Card } from "@/modern/components/ui/card";
import { cn } from "@/modern/utils";
import { TASK_BUDGET_FIELDS, REACT_EDITOR_CONTEXT_MENU_KEYS } from "@/other/constants/OtherKeys";
import { TaskBudget } from "@/bryntum-lib/models/TaskBudget";
import { Task } from "@/bryntum-lib/models/Task";
import { checkIfBudgetIdExists } from "@/network/network";
import { showToast, showArchiveRecordAlert } from "@/other/utils/utils";
import { format } from "date-fns";
import { budgetCurrencyFormat } from "@/other/utils/budgetUtils";
import { Info } from "lucide-react";

interface BudgetEditorProps {
  record: {
    budget: TaskBudget;
    onSwivuTabOnRecordChangeHandler?: (params: any) => void;
    onBudgetCreated?: (budgetId: number | string) => void;
  };
  onClose: () => void;
  editorInstance?: any;
  context?: any;
}

export const BudgetEditor = ({ record: recordProp, onClose, editorInstance }: BudgetEditorProps) => {
  const context = useSwivuBryntumContext();
  const { budget, onSwivuTabOnRecordChangeHandler, onBudgetCreated } = recordProp || {};
  const [budgetRecord, setBudgetRecord] = useState<TaskBudget>(budget);
  const [budgetIdError, setBudgetIdError] = useState<string>("");
  const [budgetIdValue, setBudgetIdValue] = useState<string>(budget?.[TASK_BUDGET_FIELDS.title] || "");
  const [linkedTasks, setLinkedTasks] = useState<Task[]>(budget?.budgetTasks || []);
  const [updateCounter, setUpdateCounter] = useState(0);
  const hasAddedToStore = useRef(false);
  const wasInitiallyPhantom = useRef(budget?.isPhantom ?? true);

  const budgetStore = context.project.taskBudgetStore;
  const taskStore = context.project.extraTaskStore;

  const isPhantom = budgetRecord?.isPhantom ?? true;
  const shouldDisableFields = wasInitiallyPhantom.current && isPhantom;

  // Get all available tasks for linking
  const availableTasks = useMemo(() => {
    const tasks = taskStore.query(() => true, true);
    return tasks.map((task: Task) => ({
      label: task.name,
      value: task.id,
    }));
  }, [taskStore]);

  // Check budget ID duplication
  const checkBudgetDuplication = useCallback(async (value: string) => {
    try {
      const boardId = (context as any).boardManager?.selectedBoard?.id;
      if (!boardId) {
        return false;
      }
      const response = await checkIfBudgetIdExists(boardId, value.trim());
      return response.data;
    } catch (error) {
      showToast({ msg: "Something went wrong", type: "error", duration: 3000 });
      console.error("Budget duplication check error:", error);
      return false;
    }
  }, [context]);

  // Handle field change
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Determine default value based on field type
    const defaultValue = typeof value === 'string' ? '' : 0;
    (budgetRecord as any)[fieldName] = value ?? defaultValue;

    // Only add to store once when phantom
    if (budgetRecord.isPhantom && !hasAddedToStore.current) {
      budgetStore.add(budgetRecord);
      hasAddedToStore.current = true;
    }

    // Force re-render by updating counter
    setUpdateCounter(prev => prev + 1);

    if (onSwivuTabOnRecordChangeHandler) {
      onSwivuTabOnRecordChangeHandler({
        record: budgetRecord,
        value,
        fieldName,
        userAction: true
      });
    }
  }, [budgetRecord, budgetStore, onSwivuTabOnRecordChangeHandler]);

  // Handle budget ID change with validation (called on blur)
  const handleBudgetIdChange = useCallback(async (value: string) => {
    if (value === budgetRecord[TASK_BUDGET_FIELDS.title]) {
      return;
    }

    if (value.trim() === "") {
      if (budgetRecord[TASK_BUDGET_FIELDS.title]) {
        handleFieldChange(TASK_BUDGET_FIELDS.title, value);
      }
      return;
    }

    const isDuplicated = await checkBudgetDuplication(value);
    if (isDuplicated) {
      setBudgetIdError("Another budget record with the same account number exists");
      return;
    }

    handleFieldChange(TASK_BUDGET_FIELDS.title, value);
  }, [budgetRecord, checkBudgetDuplication, handleFieldChange]);

  // Handle linked tasks change
  const handleLinkedTasksChange = useCallback((selectedIds: number[] | string[]) => {
    const numericIds = (selectedIds as any[]).map(id => typeof id === 'string' ? parseInt(id) : id);
    const newTasks = numericIds.map(id => taskStore.getById(id)).filter((task): task is Task => task !== undefined);

    if (showArchiveRecordAlert(budgetRecord) || showArchiveRecordAlert(newTasks)) {
      return;
    }

    budgetRecord.beginBatch();

    // Remove old links
    linkedTasks.forEach((task: Task) => {
      if (!numericIds.includes(task.id as number)) {
        (task as any).budgetId = null;
      }
    });

    // Add new links
    newTasks.forEach((task: Task) => {
      (task as any).budgetId = budgetRecord.id;
    });

    budgetRecord.endBatch();
    setLinkedTasks(newTasks);
  }, [budgetRecord, linkedTasks, taskStore]);

  // Get styled value for variance fields
  const getVarianceClassName = useCallback((value: number) => {
    if (value < 0) return "text-red-600 dark:text-red-400";
    if (value > 0) return "text-green-600 dark:text-green-500";
    return "";
  }, []);

  // Format helpers
  const formatCurrency = useCallback((value: number | undefined) => {
    if (value === undefined || value === null) return "N/A";
    return budgetCurrencyFormat.format(value);
  }, []);

  const formatPercentage = useCallback((value: number | undefined) => {
    if (value === undefined || value === null) return "N/A";
    return `${value}%`;
  }, []);

  // Read-only fields (all calculated server-side, not editable)
  const totalContract = budgetRecord[TASK_BUDGET_FIELDS.totalContract];
  const totalCostEstimated = budgetRecord[TASK_BUDGET_FIELDS.totalCostEstimated];
  const totalCostActual = budgetRecord[TASK_BUDGET_FIELDS.totalCostActual];
  const totalCostVariance = budgetRecord[TASK_BUDGET_FIELDS.totalCostVariance];
  const grossProfitEstimated = budgetRecord[TASK_BUDGET_FIELDS.grossProfitEstimated];
  const grossProfitActual = budgetRecord[TASK_BUDGET_FIELDS.grossProfitActual];
  const grossProfitVariance = budgetRecord[TASK_BUDGET_FIELDS.grossProfitVariance];
  const grossMarginEstimated = budgetRecord[TASK_BUDGET_FIELDS.grossMarginEstimated];
  const grossMarginActual = budgetRecord[TASK_BUDGET_FIELDS.grossMarginActual];
  const grossMarginVariance = budgetRecord[TASK_BUDGET_FIELDS.grossMarginVariance];
  const daysActual = budgetRecord[TASK_BUDGET_FIELDS.daysActual];
  const daysVariance = budgetRecord[TASK_BUDGET_FIELDS.daysVariance];

  // Context menu configuration - only show Archive button
  const contextMenuConfig = {
    [REACT_EDITOR_CONTEXT_MENU_KEYS.DELETE]: { hidden: true },
    [REACT_EDITOR_CONTEXT_MENU_KEYS.UNARCHIVE]: { hidden: true },
  };

  // Title display
  const titleDisplay = budgetRecord[TASK_BUDGET_FIELDS.budgetTitleDisplayValue] || budgetRecord[TASK_BUDGET_FIELDS.title] || "New Budget";

  // Footer with metadata
  const footerRenderer = () => {
    const { createdAt, updatedAt, updaterName } = budgetRecord;

    if (isPhantom || (!createdAt && !updatedAt)) {
      return null;
    }

    const dateFormat = "M/d/yyyy h:mm a";

    return (
      <>
        {createdAt && (
          <div className="flex items-center gap-1.5 text-swivu-sm text-muted-foreground">
            <i className="fas fa-calendar-plus text-[10px]" />
            <span>Created {format(new Date(createdAt), dateFormat)}</span>
          </div>
        )}
        {updatedAt && (
          <div className="flex items-center gap-1.5 text-swivu-sm text-muted-foreground">
            <i className="fas fa-clock text-[10px]" />
            <span>Updated {format(new Date(updatedAt), dateFormat)}</span>
          </div>
        )}
        {updaterName && (
          <div className="flex items-center gap-1.5 text-swivu-sm text-muted-foreground">
            <i className="fas fa-user text-[10px]" />
            <span>{updaterName}</span>
          </div>
        )}
      </>
    );
  };

  // Listen to budget store updates
  useEffect(() => {
    const detacher = budgetStore.on({
      update({ record }: { record: TaskBudget }) {
        if (record.id === budgetRecord.id) {
          // Sync the budgetIdValue with record value
          setBudgetIdValue(record[TASK_BUDGET_FIELDS.title] || "");
          // Update counter instead of setting record to avoid losing focus
          setUpdateCounter(prev => prev + 1);
        }
      },
      idChange({ record }: { record: TaskBudget }) {
        if (record.id === budgetRecord.id) {
          setBudgetRecord(record);
          setBudgetIdValue(record[TASK_BUDGET_FIELDS.title] || "");
          setUpdateCounter(prev => prev + 1);

          if (onBudgetCreated) {
            onBudgetCreated(record.id);
          }
        }
      },
    });

    return () => {
      detacher();
    };
  }, [budgetStore, budgetRecord.id, onBudgetCreated]);

  return (
    <BaseModal
      record={budgetRecord}
      onClose={onClose}
      title={titleDisplay}
      description=""
      editorInstance={editorInstance}
      hideMenu={isPhantom}
      contextMenuConfig={contextMenuConfig}
      size={{
        width: "70em",
        height: "min(100vh, 650px)",
        minWidth: "73em",
        minHeight: "600px",
        maxWidth: "100vw",
        maxHeight: "100vh"
      }}
      modal={true}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      footerRenderer={footerRenderer}
      footerClassName="justify-start items-center bg-muted/30"
    >
      <BaseModalBody className="p-6">
        <div className="flex flex-col gap-5">
          {/* Info Notice */}
          <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/30 p-4">
            <Info className="size-5 text-muted-foreground/70 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Budgets can be independent, exported, imported, and linked to any number of tasks.
            </p>
          </div>

          {/* Basic Information Card */}
          <Card variant="modern" className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-1">Basic Information</h3>
                <p className="text-xs text-muted-foreground/80">Set up the budget identifier and link to tasks</p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="budgetId">Budget ID</Label>
                  <Input
                    id="budgetId"
                    value={budgetIdValue}
                    onChange={(e) => {
                      setBudgetIdValue(e.target.value);
                      if (budgetIdError) {
                        setBudgetIdError("");
                      }
                    }}
                    onBlur={(e) => {
                      handleBudgetIdChange(e.target.value);
                    }}
                    variant="background"
                    className={cn(
                      "rounded-xl border-border/70 shadow-sm min-h-12",
                      budgetIdError && "border-destructive"
                    )}
                  />
                  {budgetIdError && (
                    <p className="text-xs text-destructive">{budgetIdError}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="linkedTask">Linked Task</Label>
                  <Combobox
                    multiSelect
                    value={linkedTasks.map(t => t.id)}
                    onValueChange={(value) => handleLinkedTasksChange(value as number[])}
                    options={availableTasks}
                    placeholder="Select tasks"
                    searchPlaceholder="Search tasks..."
                    disabled={shouldDisableFields}
                    className="min-h-12"
                    buttonSelectedClassName="min-h-12 rounded-xl border-border/70 bg-background shadow-sm"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    defaultValue={budgetRecord[TASK_BUDGET_FIELDS.description] || ""}
                    onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.description, e.target.value)}
                    disabled={shouldDisableFields}
                    variant="background"
                    className="rounded-xl border-border/70 shadow-sm min-h-12"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Contract Details Card */}
          <Card variant="modern" className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-1">Contract Details</h3>
                <p className="text-xs text-muted-foreground/80">Define contract amounts and change orders</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="contractAmount">Contract Amount</Label>
                  <Input
                    id="contractAmount"
                    type="number"
                    min={0}
                    key={updateCounter}
                    defaultValue={budgetRecord[TASK_BUDGET_FIELDS.contractAmount] || 0}
                    onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.contractAmount, parseFloat(e.target.value) || 0)}
                    disabled={shouldDisableFields}
                    variant="background"
                    className="rounded-xl border-border/70 shadow-sm min-h-12"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="changeOrders">Change Orders</Label>
                  <Input
                    id="changeOrders"
                    type="number"
                    min={0}
                    key={updateCounter}
                    defaultValue={budgetRecord[TASK_BUDGET_FIELDS.changeOrders] || 0}
                    onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.changeOrders, parseFloat(e.target.value) || 0)}
                    disabled={shouldDisableFields}
                    variant="background"
                    className="rounded-xl border-border/70 shadow-sm min-h-12"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="totalContract">Total Contract</Label>
                  <Input
                    id="totalContract"
                    value={formatCurrency(totalContract)}
                    disabled
                    readOnly
                    variant="background"
                    className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Budget Breakdown Card */}
          <Card variant="modern" className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-1">Budget Breakdown</h3>
                <p className="text-xs text-muted-foreground/80">Track estimated, actual, and variance values</p>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Estimated Column */}
                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Estimated</h4>

              <div className="flex flex-col gap-2">
                <Label htmlFor="laborEstimated">Labor Estimated</Label>
                <Input
                  id="laborEstimated"
                  type="number"
                  min={0}
                  key={`labor-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.laborEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.laborEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="materialsEstimated">Materials Estimated</Label>
                <Input
                  id="materialsEstimated"
                  type="number"
                  min={0}
                  key={`materials-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.materialsEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.materialsEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="subsEstimated">Subs Estimated</Label>
                <Input
                  id="subsEstimated"
                  type="number"
                  min={0}
                  key={`subs-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.subsEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.subsEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="rentalsEstimated">Rentals Estimated</Label>
                <Input
                  id="rentalsEstimated"
                  type="number"
                  min={0}
                  key={`rentals-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.rentalsEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.rentalsEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="miscellaneousEstimated">Miscellaneous Estimated</Label>
                <Input
                  id="miscellaneousEstimated"
                  type="number"
                  min={0}
                  key={`misc-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.miscellaneousEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.miscellaneousEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="daysEstimated">Days Estimated</Label>
                <Input
                  id="daysEstimated"
                  type="number"
                  min={0}
                  key={`days-est-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.daysEstimated] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.daysEstimated, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>
            </div>

                {/* Actual Column */}
                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Actual</h4>

              <div className="flex flex-col gap-2">
                <Label htmlFor="laborActual">Labor Actual</Label>
                <Input
                  id="laborActual"
                  type="number"
                  min={0}
                  key={`labor-act-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.laborActual] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.laborActual, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="materialsActual">Materials Actual</Label>
                <Input
                  id="materialsActual"
                  type="number"
                  min={0}
                  key={`materials-act-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.materialsActual] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.materialsActual, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="subsActual">Subs Actual</Label>
                <Input
                  id="subsActual"
                  type="number"
                  min={0}
                  key={`subs-act-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.subsActual] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.subsActual, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="rentalsActual">Rentals Actual</Label>
                <Input
                  id="rentalsActual"
                  type="number"
                  min={0}
                  key={`rentals-act-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.rentalsActual] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.rentalsActual, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="miscellaneousActual">Miscellaneous Actual</Label>
                <Input
                  id="miscellaneousActual"
                  type="number"
                  min={0}
                  key={`misc-act-${updateCounter}`}
                  defaultValue={budgetRecord[TASK_BUDGET_FIELDS.miscellaneousActual] || 0}
                  onBlur={(e) => handleFieldChange(TASK_BUDGET_FIELDS.miscellaneousActual, parseFloat(e.target.value) || 0)}
                  disabled={shouldDisableFields}
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="daysActual">Days Actual</Label>
                <Input
                  id="daysActual"
                  value={daysActual || 0}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
              </div>
            </div>

                {/* Variance Column */}
                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Variance</h4>

              <div className="flex flex-col gap-2">
                <Label htmlFor="laborVariance">Labor Variance</Label>
                <Input
                  id="laborVariance"
                  value={formatCurrency(budgetRecord[TASK_BUDGET_FIELDS.laborVariance])}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(budgetRecord[TASK_BUDGET_FIELDS.laborVariance] || 0))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="materialsVariance">Materials Variance</Label>
                <Input
                  id="materialsVariance"
                  value={formatCurrency(budgetRecord[TASK_BUDGET_FIELDS.materialsVariance])}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(budgetRecord[TASK_BUDGET_FIELDS.materialsVariance] || 0))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="subsVariance">Subs Variance</Label>
                <Input
                  id="subsVariance"
                  value={formatCurrency(budgetRecord[TASK_BUDGET_FIELDS.subsVariance])}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(budgetRecord[TASK_BUDGET_FIELDS.subsVariance] || 0))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="rentalsVariance">Rentals Variance</Label>
                <Input
                  id="rentalsVariance"
                  value={formatCurrency(budgetRecord[TASK_BUDGET_FIELDS.rentalsVariance])}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(budgetRecord[TASK_BUDGET_FIELDS.rentalsVariance] || 0))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="miscellaneousVariance">Miscellaneous Variance</Label>
                <Input
                  id="miscellaneousVariance"
                  value={formatCurrency(budgetRecord[TASK_BUDGET_FIELDS.miscellaneousVariance])}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(budgetRecord[TASK_BUDGET_FIELDS.miscellaneousVariance] || 0))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="daysVariance">Days Variance</Label>
                <Input
                  id="daysVariance"
                  value={daysVariance || 0}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(daysVariance || 0))}
                />
                </div>
              </div>
            </div>
            </div>
          </Card>

          {/* Summary Totals Card */}
          <Card variant="modern" className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-1">Summary Totals</h3>
                <p className="text-xs text-muted-foreground/80">Overall costs, profit, and margin calculations</p>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Estimated Totals */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                <Label htmlFor="totalCostEstimated">Total Costs Estimated</Label>
                <Input
                  id="totalCostEstimated"
                  value={formatCurrency(totalCostEstimated)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
              </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossProfitEstimated">Gross Profit Estimated</Label>
                <Input
                  id="grossProfitEstimated"
                  value={formatCurrency(grossProfitEstimated)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossMarginEstimated">Gross Margin Estimated</Label>
                <Input
                  id="grossMarginEstimated"
                  value={formatPercentage(grossMarginEstimated)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
                  </div>
                </div>

                {/* Actual Totals */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                <Label htmlFor="totalCostActual">Total Cost Actual</Label>
                <Input
                  id="totalCostActual"
                  value={formatCurrency(totalCostActual)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossProfitActual">Gross Profit Actual</Label>
                <Input
                  id="grossProfitActual"
                  value={formatCurrency(grossProfitActual)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossMarginActual">Gross Margin Actual</Label>
                <Input
                  id="grossMarginActual"
                  value={formatPercentage(grossMarginActual)}
                  disabled
                  readOnly
                  variant="background"
                  className="rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50"
                />
                  </div>
                </div>

                {/* Variance Totals */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                <Label htmlFor="totalCostVariance">Total Cost Variance</Label>
                <Input
                  id="totalCostVariance"
                  value={formatCurrency(totalCostVariance)}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(totalCostVariance || 0))}
                />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossProfitVariance">Gross Profit Variance</Label>
                <Input
                  id="grossProfitVariance"
                  value={formatCurrency(grossProfitVariance)}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(grossProfitVariance || 0))}
                />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grossMarginVariance">Gross Margin Variance</Label>
                <Input
                  id="grossMarginVariance"
                  value={formatPercentage(grossMarginVariance)}
                  disabled
                  readOnly
                  variant="background"
                  className={cn("rounded-xl border-border/70 shadow-sm min-h-12 bg-muted/50", getVarianceClassName(grossMarginVariance || 0))}
                />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};
